import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "calendar-day-item-footer"
}
const _hoisted_2 = { class: "calendar-day-footer-label" }
const _hoisted_3 = { class: "item-date-wrap" }
const _hoisted_4 = { class: "item-day" }

import { computed, ref } from 'vue';
import format from 'date-fns/format';
import isFirstDayOfMonth from 'date-fns/isFirstDayOfMonth';
import getDate from 'date-fns/getDate';
import isWeekend from 'date-fns/isWeekend';
import startOfMonth from 'date-fns/startOfMonth';
import getWeekOfMonth from 'date-fns/getWeekOfMonth';
import endOfDay from 'date-fns/endOfDay';
import getTime from 'date-fns/getTime';
import millisecondsToSeconds from 'date-fns/millisecondsToSeconds';
import QuickCardButton from '@/components/editor/toolbar/sections/QuickCardButton.vue';
import { browserStorageSetItem, browserStorageGetItem } from '@/utilities/browser-storage.util';
import { getQuickCardCreationDraftKey } from '@/utilities/localDrafts';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import CalendarCardsTooltip from './tooltips/CalendarCardsTooltip.vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarDayItem',
  props: {
  dayIndex: {
    type: Number,
    required: true,
  },
  dateTimestamp: {
    type: Number,
    required: true,
  },
  dayHumanized: {
    type: String,
    required: true,
  },
  isToday: {
    type: Boolean,
    default: false,
  },
  isCurrentMonth: {
    type: Boolean,
    default: false,
  },
  isPast: {
    type: Boolean,
    default: false,
  },
  isFuture: {
    type: Boolean,
    default: false,
  },
  dayIsWeekend: {
    type: Boolean,
    default: false,
  },
  maxCardsVisible: {
    type: Number,
    required: true,
  },
  showMoreCardsCount: {
    type: Number,
    default: 0,
  },
  moreCards: {
    type: Array,
    default: () => [],
  },
  weekId: {
    type: Number,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const store = useStore();

const { iconPath, getCustomIconStyle } = useCustomIcon();
const { translate } = useTranslate();
const { inEpicBoard } = useBoardEmbed();
const { isGuestReader } = useCurrentRole();

const displayShowMorePopup = ref(false);
const documentBody = ref(document.body);
const showMoreButton = ref<HTMLElement | null>(null);
const isHovered = ref(false);

const itemBackgroundStyle = computed(() => {
  return {
    gridArea: `1 / ${props.dayIndex + 1} / span ${props.maxCardsVisible + 3} / span 1`,
  };
});

const itemHeaderStyle = computed(() => {
  return {
    gridArea: `1 / ${props.dayIndex + 1} / span 1 / span 1`,
  };
});

const footerIconStyle = computed(() => {
  return getCustomIconStyle(iconPath('chevron-down'));
});

const monthLabel = computed(() => {
  return format(props.dateTimestamp, 'LLL', { weekStartsOn: 1 });
});

const showWeekend = computed(() => store.getters.getShowWeekends);

// show month label for first day of the month
// or if weekend is disabled, show month label for the first day of the first week of the month
const showMonthLabel = computed(() => {
  const { dateTimestamp, dayIndex } = props;
  return (
    isFirstDayOfMonth(dateTimestamp) ||
    (!showWeekend.value &&
      dayIndex === 0 &&
      isWeekend(startOfMonth(dateTimestamp)) &&
      getWeekOfMonth(dateTimestamp, { weekStartsOn: 1 }) === 2)
  );
});

// isLongMonth is boolean, true if the index is 0 and it is first week of the month
// const isLongMonth = computed(() => {
//   return false;
// });

const monthClass = computed(() => {
  return 'item-short-month';
});

const calendarDayItemClass = computed(() => {
  return {
    today: props.isToday,
    'hover-active': isHovered.value,
  };
});

const dayLabel = computed(() => {
  return getDate(props.dateTimestamp);
});

const quickCard = computed(() => store.getters.getQuickCard);

const getDatesForQuickCard = () => {
  const start_date = millisecondsToSeconds(props.dateTimestamp);
  const due_date = millisecondsToSeconds(getTime(endOfDay(props.dateTimestamp)));
  return { start_date, due_date };
};

const currentTeamId = computed(() => store.getters.getTeamID);

const moreCardsButtonLabel = computed(() => {
  return displayShowMorePopup.value ? translate('hideAllCards') : translate('showAllCards');
});

const handleQuickCardButtonClick = () => {
  const quickCardDraftKey = getQuickCardCreationDraftKey(currentTeamId.value);
  const existingQuickCard = browserStorageGetItem(quickCardDraftKey);
  const newQuickCardObject = existingQuickCard
    ? { ...existingQuickCard, ...getDatesForQuickCard() }
    : { ...quickCard.value, ...getDatesForQuickCard() };

  if (!existingQuickCard) {
    store.dispatch('setQuickCardObject', { quickCardObject: newQuickCardObject });
  } else {
    browserStorageSetItem(quickCardDraftKey, newQuickCardObject);
  }

  store.dispatch('setQuickCard', true);
};

const handleMouseOver = () => {
  if (isHovered.value) return;
  isHovered.value = true;
};

const handleMouseLeave = () => {
  if (!isHovered.value) return;
  isHovered.value = false;
};

const getShowMorePopupStyle = () => {
  if (!showMoreButton.value) return;

  const width = `${showMoreButton.value?.$el.clientWidth}px`;
  return {
    maxWidth: width,
    width,
  };
};

const onDropdownStateChange = (state: any) => {
  displayShowMorePopup.value = state.isVisible;
};

return (_ctx: any,_cache: any) => {
  const _component_v_popover = _resolveComponent("v-popover")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["calendar-day-item", calendarDayItemClass.value]),
    onMouseover: handleMouseOver,
    onMouseleave: handleMouseLeave
  }, [
    _createElementVNode("div", {
      class: "calendar-day-item-background",
      style: _normalizeStyle(itemBackgroundStyle.value)
    }, [
      (__props.showMoreCardsCount)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_v_popover, {
              ref_key: "showMoreButton",
              ref: showMoreButton,
              interactive: "",
              tag: "div",
              "content-tag": "div",
              trigger: 'click',
              role: 'poper',
              placement: 'bottom-start',
              "append-to": () => documentBody.value,
              class: _normalizeClass(["calendar-day-item-footer-button", { 'show-more-active': displayShowMorePopup.value }]),
              onState: onDropdownStateChange
            }, {
              content: _withCtx(({ state, hide }) => [
                (state.isVisible)
                  ? (_openBlock(), _createBlock(CalendarCardsTooltip, {
                      key: 0,
                      "card-list": __props.moreCards,
                      "max-cards-visible": __props.maxCardsVisible,
                      "week-id": __props.weekId,
                      style: _normalizeStyle(getShowMorePopupStyle()),
                      onCloseTippy: hide
                    }, null, 8, ["card-list", "max-cards-visible", "week-id", "style", "onCloseTippy"]))
                  : _createCommentVNode("", true)
              ]),
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_2, _toDisplayString(moreCardsButtonLabel.value), 1),
                _cache[0] || (_cache[0] = _createTextVNode()),
                _createElementVNode("em", {
                  class: _normalizeClass(["custom-icons calendar-day-footer-icon", { 'rotate-180': displayShowMorePopup.value }]),
                  style: _normalizeStyle(footerIconStyle.value)
                }, null, 6),
                _cache[1] || (_cache[1] = _createTextVNode())
              ]),
              _: 1
            }, 8, ["append-to", "class"])
          ]))
        : _createCommentVNode("", true)
    ], 4),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createElementVNode("div", {
      class: _normalizeClass(["calendar-day-item-header usn", { 'item-past': __props.isPast }]),
      style: _normalizeStyle(itemHeaderStyle.value)
    }, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, _toDisplayString(dayLabel.value), 1),
        _cache[2] || (_cache[2] = _createTextVNode()),
        (showMonthLabel.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(monthClass.value)
            }, _toDisplayString(monthLabel.value), 3))
          : _createCommentVNode("", true)
      ]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (isHovered.value && !_unref(inEpicBoard) && !_unref(isGuestReader))
        ? (_openBlock(), _createBlock(QuickCardButton, {
            key: 0,
            effect: handleQuickCardButtonClick
          }))
        : _createCommentVNode("", true)
    ], 6)
  ], 34))
}
}

})